import { useCallback, useId, useState } from 'react';
import { MAX_MIN_WIDTH, X0 } from '@/layout/constants';
import type { LaidOutNode, LaidOutRibbon, Layout, StageId, ViewId } from '@/layout/types';
import { formatCount, formatPercent } from '@/lib/format';
import { FlowNode } from './FlowNode';
import { Ribbon } from './Ribbon';
import { Tooltip, type TooltipContent } from '../Tooltip';

interface Props {
  layout: Layout;
  selectedStage: StageId | null;
  /** Exploded views are the stage, so selection dimming is meaningless there. */
  selectable: boolean;
  onActivate: (target: { stage?: StageId; explode?: ViewId }) => void;
}

function nodeTooltip(node: LaidOutNode): TooltipContent {
  return {
    title: node.title,
    body: `${formatCount(node.display)} people · ${formatPercent(node.share)} ${node.shareLabel}`,
    notes: [node.detail, node.explode ? 'Click to explode this step' : 'Click to edit this stage']
      .filter((v): v is string => Boolean(v)),
  };
}

function ribbonTooltip(ribbon: LaidOutRibbon): TooltipContent {
  return {
    title: ribbon.title,
    body:
      `${formatCount(ribbon.value)} people · ${formatPercent(ribbon.share)} ` +
      `of ${ribbon.denominatorLabel}`,
    notes:
      ribbon.kind === 'overlay'
        ? ['Overlapping work — these people do not leave the funnel']
        : [],
  };
}

export function Sankey({ layout, selectedStage, selectable, onActivate }: Props) {
  const titleId = useId();
  const descId = useId();
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ content: TooltipContent; x: number; y: number } | null>(
    null,
  );

  const hoverNode = useCallback(
    (node: LaidOutNode | null, event?: React.MouseEvent | React.FocusEvent) => {
      if (!node) {
        setHoverKey(null);
        setTooltip(null);
        return;
      }
      setHoverKey(node.key);
      const point =
        event && 'clientX' in event
          ? { x: event.clientX, y: event.clientY }
          : elementCenter(event?.currentTarget as Element | undefined);
      setTooltip({ content: nodeTooltip(node), ...point });
    },
    [],
  );

  const hoverRibbon = useCallback((ribbon: LaidOutRibbon | null, event?: React.MouseEvent) => {
    if (!ribbon || !event) {
      setHoverKey(null);
      setTooltip(null);
      return;
    }
    setHoverKey(ribbon.key);
    setTooltip({ content: ribbonTooltip(ribbon), x: event.clientX, y: event.clientY });
  }, []);

  const isSelected = (stage?: StageId) => selectable && stage != null && stage === selectedStage;
  const hasSelection = selectable && selectedStage != null;
  const paletteClass = layout.palette === 'semantic' ? '' : ` is-${layout.palette}`;

  /** Hover dims everything else; with no hover, an open stage dims the rest. */
  const dimmed = (key: string, stage?: StageId) => {
    if (hoverKey) return key !== hoverKey;
    if (hasSelection) return !isSelected(stage);
    return false;
  };

  return (
    <>
      <svg
        className={`flow-svg${paletteClass}`}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        style={{ maxWidth: layout.width, minWidth: Math.min(MAX_MIN_WIDTH, layout.width) }}
        role="group"
        aria-labelledby={`${titleId} ${descId}`}
        onMouseLeave={() => {
          setHoverKey(null);
          setTooltip(null);
        }}
      >
        <title id={titleId}>{layout.title}</title>
        <desc id={descId}>{layout.desc}</desc>

        <g className="stage-labels">
          {layout.columns.map((column) => (
            <text key={column.label} className="stage-label" x={column.centerX} y={layout.labelY}>
              {column.label.toUpperCase()}
            </text>
          ))}
        </g>

        <g className="gate-notes">
          {layout.gates.map((gate) => (
            <text key={gate.key} className="gate-note" x={gate.x} y={layout.labelY + 38}>
              ×{formatPercent(gate.rate)}
            </text>
          ))}
        </g>

        <line
          className="stage-rule"
          x1={X0}
          y1={layout.ruleY}
          x2={layout.width - X0}
          y2={layout.ruleY}
        />

        {layout.difficultyBreak && (
          <DifficultyBreak
            x={layout.difficultyBreak.x}
            top={layout.ruleY + 32}
            bottom={layout.height - 22}
          />
        )}

        <g className="ribbons">
          {layout.ribbons.map((ribbon, i) => (
            <Ribbon
              key={`${ribbon.key}-${i}`}
              ribbon={ribbon}
              dimmed={dimmed(ribbon.key, ribbon.stage)}
              selected={isSelected(ribbon.stage)}
              onActivate={(r) => onActivate({ stage: r.stage, explode: r.explode })}
              onHover={hoverRibbon}
            />
          ))}
        </g>

        <g className="nodes">
          {layout.nodes.map((node) => (
            <FlowNode
              key={node.key}
              node={node}
              dimmed={dimmed(node.key, node.stage)}
              selected={isSelected(node.stage)}
              onActivate={(n) => onActivate({ stage: n.stage, explode: n.explode })}
              onHover={hoverNode}
            />
          ))}
        </g>
      </svg>

      {tooltip && <Tooltip content={tooltip.content} x={tooltip.x} y={tooltip.y} />}
    </>
  );
}

function elementCenter(element?: Element): { x: number; y: number } {
  if (!element) return { x: 0, y: 0 };
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.bottom };
}

function DifficultyBreak({ x, top, bottom }: { x: number; top: number; bottom: number }) {
  const span = bottom - top;
  const path =
    `M ${x} ${top}` +
    ` C ${x - 3} ${top + span * 0.14}, ${x + 3} ${top + span * 0.24}, ${x - 1} ${top + span * 0.38}` +
    ` S ${x + 4} ${top + span * 0.62}, ${x} ${top + span * 0.74}` +
    ` S ${x - 3} ${top + span * 0.91}, ${x + 1} ${bottom}`;

  return (
    <g className="difficulty-break" aria-hidden="true">
      <rect
        className="difficulty-break__wash"
        x={x - 10}
        y={top}
        width={20}
        height={span}
        rx={10}
      />
      <path className="difficulty-break__line" d={path} />
    </g>
  );
}
